import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  WaterQualityIndexSelectors,
  WQIViolation,
} from '../../../../store/waterQualityIndex'
import { WaterTestKitRequestsSelectors } from '../../../../store/waterTestKitRequests'
import { PillarCard, PillarKey } from './PillarCard'

const PILLARS: PillarKey[] = ['test', 'monitor', 'protect']
const TAB_LABELS: Record<PillarKey, string> = {
  test: 'TEST',
  monitor: 'MONITOR',
  protect: 'PROTECT',
}

const MICROBIOLOGICAL_KEYWORDS = [
  'e. coli',
  'e.coli',
  'total coliform',
  'coliform',
  'turbidity',
  'legionella',
]
const COMMUNICATION_KEYWORDS = [
  'public notice',
  'consumer confidence',
]

// SIPP-826: health-based violation keywords — anything that affects water
// safety at the tap as distinct from pure reporting/notice failures. Covers
// MCL + TT breaches, microbiological pathogens, and the common named
// contaminants flagged in SDWIS violation categories.
const HEALTH_BASED_KEYWORDS = [
  ...MICROBIOLOGICAL_KEYWORDS,
  'mcl',
  'maximum contaminant level',
  'treatment technique',
  'lead',
  'copper',
  'arsenic',
  'nitrate',
  'nitrite',
  'radionuclide',
  'uranium',
  'radium',
  'disinfection byproduct',
  'trihalomethane',
  'haloacetic',
  'volatile organic',
  'synthetic organic',
  'pfas',
  'health',
]

const matchesKeyword = (value: string | undefined, keywords: string[]) => {
  if (!value) return false
  const lowered = value.toLowerCase()
  return keywords.some((kw) => lowered.includes(kw))
}

const hasViolationMatch = (violations: WQIViolation[] | undefined, keywords: string[]) => {
  if (!violations?.length) return false
  return violations.some(
    (v) =>
      matchesKeyword(v.violationCategory, keywords) ||
      matchesKeyword(v.contaminantName, keywords)
  )
}

export const TestMonitorProtectSection = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const waterSafetyStatus = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)
  const waterSourceType = useSelector(WaterQualityIndexSelectors.selectWaterSourceType)
  const reputation = useSelector(WaterQualityIndexSelectors.selectUtilityReputation)
  const utility = useSelector(WaterQualityIndexSelectors.selectUtility)
  const counts = useSelector(WaterQualityIndexSelectors.selectTestResultCounts)
  const latestTestKitRequest = useSelector(WaterTestKitRequestsSelectors.makeLatestResult)
  const groupedSignals = useSelector(
    WaterQualityIndexSelectors.selectContributorsByUICategory
  )

  // SIPP-828: exclusive-none from the test strip equipment question.
  const userHasNoFilter =
    Array.isArray(latestTestKitRequest?.systems) &&
    latestTestKitRequest!.systems.length === 1 &&
    latestTestKitRequest!.systems[0] === 'none'

  // SIPP-841: home age string carried through from the test kit request.
  const homeAge = latestTestKitRequest?.home_age ?? null

  // SIPP-841: chloramine + plumbing stress signal indicates PEX-era homes
  // are at elevated corrosion risk. Flatten signal contributors and look for
  // the dedicated signalKey emitted by the WQI engine.
  const hasChloramineStress = useMemo(
    () =>
      groupedSignals.some((group) =>
        group.contributors.some(
          (signal) => signal.signalKey === 'chloramine_plumbing_stress_context'
        )
      ),
    [groupedSignals]
  )

  const {
    utilityHasMicrobiologicalViolations,
    utilityHasCommunicationViolations,
    utilityHasHealthBasedViolations,
  } = useMemo(
    () => ({
      utilityHasMicrobiologicalViolations: hasViolationMatch(
        utility?.violations,
        MICROBIOLOGICAL_KEYWORDS
      ),
      utilityHasCommunicationViolations: hasViolationMatch(
        utility?.violations,
        COMMUNICATION_KEYWORDS
      ),
      utilityHasHealthBasedViolations: hasViolationMatch(
        utility?.violations,
        HEALTH_BASED_KEYWORDS
      ),
    }),
    [utility?.violations]
  )

  const [activeTab, setActiveTab] = useState(0)

  const pillarProps = (pillar: PillarKey) => ({
    pillar,
    waterSafetyStatus,
    waterSourceType,
    utilityViolationCount: reputation.violationCount,
    utilityHasMicrobiologicalViolations,
    utilityHasCommunicationViolations,
    utilityHasHealthBasedViolations,
    userHasNoFilter,
    homeAge,
    hasChloramineStress,
    unsafeCount: counts.unsafe,
    cautiousCount: counts.cautious,
  })

  return (
    <Stack spacing={2.5} sx={{ width: '100%' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 2,
          p: { xs: 2.5, md: 4 },
          width: '100%',
        }}
      >
        <Typography
          sx={{
            color: 'common.white',
            letterSpacing: 1,
            typography: { xs: 'h4', md: 'h3' },
          }}
        >
          TEST. MONITOR. PROTECT.
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.85)', mt: 1 }}
        >
          The three things that matter most between now and your next test — and what SIPP does about each one.
        </Typography>
      </Box>

      {isMobile ? (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {PILLARS.map((p, i) => (
              <Box
                key={p}
                onClick={() => setActiveTab(i)}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 1,
                  borderRadius: 5,
                  cursor: 'pointer',
                  bgcolor: activeTab === i ? 'primary.dark' : 'grey.200',
                  color: activeTab === i ? 'common.white' : 'text.secondary',
                  typography: 'body2',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                }}
              >
                {TAB_LABELS[p]}
              </Box>
            ))}
          </Stack>
          <PillarCard {...pillarProps(PILLARS[activeTab])} />
        </>
      ) : (
        <Grid container spacing={2}>
          {PILLARS.map((p) => (
            <Grid key={p} size={{ xs: 12, md: 4 }}>
              <PillarCard {...pillarProps(p)} />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  )
}
